import {
  Directive,
  Input,
  inject,
  signal,
  input
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseInputStatusDirective } from '../form/input-status/base-input-status.directive';

import type { Signal, WritableSignal } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';

@Directive({
  selector: 'input[tdWbInput], textarea[tdWbInput], td-wb-file-upload[tdWbInput]',
  exportAs: 'tdWbInput',
  host: {
    '[attr.role]': 'role',
    '[class.td-wb-input]': 'true',
    '[class.td-wb-input--focus]': 'focusState',
    '[class.td-wb-input--hover]': 'hoverState',
    '[attr.aria-describedby]': 'describedBy',
    '[attr.aria-invalid]': 'ariaInvalid',
    '[attr.autocomplete]': 'autoComplete() ?? "off"',
    '[class]': '[className(), inputClass].join(\' \')',
    '[class.td-wb-error]': 'showErrorClass',
    '[attr.disabled]': 'disabled ? "" : null'
  }
})
export class InputDirective extends BaseInputStatusDirective {
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    this.disabledSignal.set(value);
  }

  get disabled(): boolean {
    return this._disabled;
  }

  readonly className = input<string>(undefined, { alias: 'class' });
  readonly autoComplete = input<string | undefined>(undefined, { alias: 'autocomplete' });

  role: string;
  focusState: boolean = false;
  hoverState: boolean = false;
  describedBy: string;
  ariaInvalid: string;

  get showErrorClass(): boolean {
    return this.inputGroupField === null ? this.control.invalid : this.isErrorState();
  }

  inputClass: string;

  /** Reactive signal tracking the current control value. */
  readonly currentValue: Signal<unknown>;

  /** Reactive signal tracking the disabled state. */
  readonly disabledSignal: WritableSignal<boolean> = signal(false);

  private readonly control: UntypedFormControl;
  private _disabled: boolean;

  constructor() {
    super();
    const ngModel = inject(NgModel, { self: true, optional: true });
    this.control = ngModel?.control;
    this.currentValue = this.control
      ? toSignal(this.control.valueChanges, { initialValue: this.control.value })
      : signal(undefined);
  }

  setFocusState(focusState: boolean): void {
    if (!this.disabled) {
      this.focusState = focusState;
    }
  }

  setHoverState(hoverState: boolean): void {
    if (!this.disabled) {
      this.hoverState = hoverState;
    }
  }

  getValue(): string | number {
    return this.control.value;
  }

  setValue(value: unknown): void {
    this.control.setValue(value);
  }

  clear(): void {
    this.control.setValue(undefined);
  }

}
